import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BOOKING_CONTEXT, CURRENCY, SELECTED_CURRENCY_STORAGE_KEY } from '~/literals';

import { AppState } from '~/types';

const GDPR_CONSENT_RECORD_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

interface SubmitGdprConsentRecordPayload {
  consentVersion?: string;
}

export const appState: AppState = {
  query: 'react',
  route: null,
  bookingContext: BOOKING_CONTEXT.FLIGHT,
  currency: CURRENCY.USD,
  disableCurrencySelector: false,
  acceptedCookies: null,
  gdprConsentRecordStatus: GDPR_CONSENT_RECORD_STATUS.IDLE,
  gdprConsentRecordErrorMessage: null,
  recommendedCurrency: null,
  loadingRecommendedCurrency: false,
};

export const appSlice = createSlice({
  name: 'app',
  initialState: appState,
  reducers: {
    setAppOptions: (draft, { payload }) => {
      draft.query = payload.query;
    },
    updateRoute: (draft, { payload }) => {
      draft.route = payload;
    },
    updateCurrency: (draft, { payload }) => {
      draft.currency = payload;
      localStorage.setItem(SELECTED_CURRENCY_STORAGE_KEY, payload);
    },
    updateDisableCurrencySelector: (draft, { payload }) => {
      draft.disableCurrencySelector = payload;
    },
    updateBookingContext: (draft, { payload }) => {
      draft.bookingContext = payload;
    },
    updateAcceptedCookies: (draft) => {
      draft.acceptedCookies = true;
    },
    updateDeniedCookies: (draft) => {
      draft.acceptedCookies = false;
    },
    submitGdprConsentRecord: (draft, _action: PayloadAction<SubmitGdprConsentRecordPayload | undefined>) => {
      draft.gdprConsentRecordStatus = GDPR_CONSENT_RECORD_STATUS.LOADING;
      draft.gdprConsentRecordErrorMessage = null;
    },
    submitGdprConsentRecordSuccess: (draft) => {
      draft.gdprConsentRecordStatus = GDPR_CONSENT_RECORD_STATUS.SUCCESS;
      draft.gdprConsentRecordErrorMessage = null;
    },
    submitGdprConsentRecordFailure: (draft, { payload }) => {
      draft.gdprConsentRecordStatus = GDPR_CONSENT_RECORD_STATUS.FAILURE;
      draft.gdprConsentRecordErrorMessage = payload?.errorMessage || 'Failed to store GDPR consent record.';
    },
    fetchRecommendedCurrency: (draft) => {
      draft.loadingRecommendedCurrency = true;
    },
    fetchRecommendedCurrencySuccess: (draft, { payload }) => {
      draft.currency = payload;
      localStorage.setItem(SELECTED_CURRENCY_STORAGE_KEY, payload);
      draft.loadingRecommendedCurrency = false;
    },
    fetchRecommendedCurrencyFailure: (draft) => {
      draft.currency = CURRENCY.USD;
      draft.loadingRecommendedCurrency = false;
    },
  },
});

export const {
  setAppOptions,
  updateRoute,
  updateBookingContext,
  updateCurrency,
  updateDisableCurrencySelector,
  updateAcceptedCookies,
  updateDeniedCookies,
  submitGdprConsentRecord,
  submitGdprConsentRecordSuccess,
  submitGdprConsentRecordFailure,
  fetchRecommendedCurrency,
  fetchRecommendedCurrencySuccess,
  fetchRecommendedCurrencyFailure,
} = appSlice.actions;
export default appSlice.reducer;
